import { AssetType, ExternalAssetSearchDto } from '@unistocks/proxy/axios-client';
import { assetsSearchV2 } from '@unistocks/proxy/axios-client/AssetsClient';
import { MAX_RESULT_COUNT_SEARCH } from '@unistocks/shared/consts';
import { formatISINAndSymbol } from '@unistocks/shared/utils/asset-utils';
import { arrayify } from '@unistocks/shared/utils/util';
import { MaybeArray, SelectOption } from '@unistocks/types';
import { useCallback, useEffect, useRef } from 'react';

/**
 * Hook to search for assets with cancellation support
 * @param assetTypes The types of assets to search for
 * @param limit The maximum number of results to return
 */
export const useAssetSearch = (
  assetTypes: MaybeArray<AssetType>,
  limit = MAX_RESULT_COUNT_SEARCH
) => {
  const assetSearchAbortController = useRef<AbortController>(null);

  const search = useCallback(
    async (search: string) => {
      if (assetSearchAbortController.current) {
        assetSearchAbortController.current.abort();
      }

      assetSearchAbortController.current = new AbortController();

      const { signal } = assetSearchAbortController.current;

      const { items } = await assetsSearchV2(search, arrayify(assetTypes), limit, {
        signal
      });

      return (
        items?.map(
          asset =>
            ({
              key: `${asset.dataSource}-${asset.identifier}`,
              value: asset,
              title: asset.name,
              subTitle: formatISINAndSymbol(asset)
            }) as SelectOption<ExternalAssetSearchDto>
        ) ?? []
      );
    },
    [assetTypes, limit]
  );

  const abort = useCallback(() => assetSearchAbortController.current?.abort(), []);

  useEffect(() => {
    return () => {
      abort();
    };
  }, [abort]);

  return {
    search,
    abort
  };
};
